import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Button, IconButton, CloseIcon } from './Button';
import useModalFocus from './useModalFocus';
import { CONFIRM_GUARD_MS } from '../shared/CancelTaskConfirm';
import './ui.css';

/**
 * Centred modal dialog: scrim, focus trap, Escape, focus return. Rendered into
 * <body> under its own `ui-v2` scope and marked `data-pane-overlay`.
 */
export function Dialog({
    open, onClose, title, children, footer, wide = false, role = 'dialog',
    describedBy, initialFocusRef, hideClose = false,
}) {
    const titleId = `${useId().replace(/:/g, '')}-dialog-title`;
    const panelRef = useRef(null);
    const layerRef = useRef(null);
    const onKeyDown = useModalFocus(panelRef, open, { onEscape: onClose, initialFocusRef, layerRef });

    if (!open) return null;
    return createPortal(
        <div className="ui-v2 v2-layer" data-pane-overlay="true" ref={layerRef}>
            {/* preventDefault: a mousedown on the scrim would otherwise move focus
                to <body> after the close, undoing the focus return (QA B5). */}
            <div className="v2-scrim v2-scrim--modal" onMouseDown={(e) => { e.preventDefault(); onClose(); }} />
            <div
                ref={panelRef}
                className={`v2-modal${wide ? ' v2-modal--wide' : ''}`}
                role={role}
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={describedBy}
                onKeyDown={onKeyDown}
            >
                <div className="v2-md-head">
                    <h2 id={titleId}>{title}</h2>
                    {!hideClose && <IconButton label="Close" onClick={onClose}><CloseIcon /></IconButton>}
                </div>
                <div className="v2-md-body">{children}</div>
                {footer && <div className="v2-md-foot">{footer}</div>}
            </div>
        </div>,
        document.body,
    );
}

Dialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.node.isRequired,
    children: PropTypes.node,
    footer: PropTypes.node,
    wide: PropTypes.bool,
    role: PropTypes.oneOf(['dialog', 'alertdialog']),
    describedBy: PropTypes.string,
    initialFocusRef: PropTypes.object,
    hideClose: PropTypes.bool,
};

/**
 * "Are you sure?" dialog. The safe choice comes first and takes focus, and the
 * confirm button ignores the second click of a double-click and any click
 * within CONFIRM_GUARD_MS of opening — the CancelTaskConfirm pattern.
 */
export function ConfirmDialog({
    open, title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', danger = false, busy = false,
    onConfirm, onCancel,
}) {
    const msgId = `${useId().replace(/:/g, '')}-confirm-msg`;
    const cancelRef = useRef(null);
    const openedAt = useRef(0);

    useEffect(() => {
        if (open) openedAt.current = Date.now();
    }, [open]);

    const handleConfirm = (e) => {
        if (e.detail > 1 || Date.now() - openedAt.current < CONFIRM_GUARD_MS) return;
        onConfirm();
    };

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            title={title}
            role="alertdialog"
            describedBy={msgId}
            initialFocusRef={cancelRef}
            hideClose
            footer={(
                <>
                    <Button ref={cancelRef} onClick={onCancel}>{cancelLabel}</Button>
                    <Button variant={danger ? 'danger' : 'primary'} onClick={handleConfirm} disabled={busy}>
                        {confirmLabel}
                    </Button>
                </>
            )}
        >
            <p id={msgId} className="v2-md-msg">{message}</p>
        </Dialog>
    );
}

ConfirmDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    title: PropTypes.node.isRequired,
    message: PropTypes.node.isRequired,
    confirmLabel: PropTypes.node,
    cancelLabel: PropTypes.node,
    danger: PropTypes.bool,
    busy: PropTypes.bool,
    onConfirm: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};
